import type { QueueState } from "../api/generated";

/** Длительность словами: «4 мин», а не «247 с». */
export function humanDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} с`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч`;
  return `${Math.floor(seconds / 86400)} сут`;
}

const fileSizeNumberFormat = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 1,
});

export function formatBytes(size: number): string {
  if (size < 1024 * 1024) return `${fileSizeNumberFormat.format(size)} Б`;
  if (size < 1024 * 1024 * 1024)
    return `${fileSizeNumberFormat.format(size / (1024 * 1024))} МБ`;
  return `${fileSizeNumberFormat.format(size / (1024 * 1024 * 1024))} ГБ`;
}

/**
 * Российский номер в привычном виде.
 *
 * Люди набирают номер как придётся: с восьмёрки, без скобок, со скрытыми
 * пробелами. Приводим к +7 и расставляем разделители по мере набора; чужие
 * номера (не 7 и не 8 в начале) оставляем как есть, чтобы не мешать вводу.
 */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits === "") return "";
  if (!/^[78]/.test(digits)) return value.trim();

  const rest = digits.slice(1, 11);
  const parts = [
    rest.slice(0, 3),
    rest.slice(3, 6),
    rest.slice(6, 8),
    rest.slice(8, 10),
  ].filter(Boolean);
  if (parts.length === 0) return "+7";
  const [code, first, second, third] = parts;
  return (
    `+7 (${code}` +
    (parts.length > 1 ? `) ${first}` : "") +
    (second ? `-${second}` : "") +
    (third ? `-${third}` : "")
  );
}

export { uploadMime } from "../entities/files";

/**
 * Объяснение вместо ярлыка.
 *
 * «degraded» ничего не сообщает тому, кто должен решить, что делать. Строка
 * ниже называет очередь по-русски и говорит, сколько именно задача ждёт.
 */
export function queueExplanation(queue: QueueState): string {
  if (queue.status === "healthy") {
    return queue.depth === 0 ? "Пусто" : `${queue.depth} в работе`;
  }
  const waiting = `старейшая задача ждёт ${humanDuration(queue.oldest_age_seconds)}`;
  return queue.status === "stalled"
    ? `Очередь стоит: ${queue.depth} задач, ${waiting}.`
    : `Очередь отстаёт: ${queue.depth} задач, ${waiting}.`;
}

const FILE_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Документ Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Документ Word",
  "application/vnd.ms-excel": "Таблица Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Таблица Excel",
  "application/vnd.ms-powerpoint": "Презентация PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "Презентация PowerPoint",
  "application/rtf": "Документ RTF",
  "application/vnd.oasis.opendocument.text": "Документ OpenDocument",
  "application/vnd.oasis.opendocument.spreadsheet": "Таблица OpenDocument",
  "application/vnd.oasis.opendocument.presentation": "Презентация OpenDocument",
  "image/jpeg": "Изображение JPEG",
  "image/png": "Изображение PNG",
  "image/webp": "Изображение WebP",
  "image/gif": "Изображение GIF",
  "text/plain": "Текстовый файл",
  "text/csv": "Таблица CSV",
  "text/markdown": "Файл Markdown",
  "text/html": "HTML-страница",
  "text/xml": "Файл XML",
  "application/xml": "Файл XML",
  "application/json": "Файл JSON",
  "application/zip": "Архив ZIP",
  "application/vnd.rar": "Архив RAR",
  "application/x-7z-compressed": "Архив 7z",
  "application/gzip": "Архив GZIP",
  "application/x-tar": "Архив TAR",
  "message/rfc822": "Письмо",
  "image/svg+xml": "Изображение SVG",
  "image/bmp": "Изображение BMP",
  "image/tiff": "Изображение TIFF",
  "image/heic": "Изображение HEIC",
  "image/vnd.dwg": "Чертёж DWG",
  "image/vnd.dxf": "Чертёж DXF",
  "application/pkcs7-signature": "Электронная подпись",
  "application/octet-stream": "Файл без известного формата",
};

/**
 * Название формата словами.
 *
 * В карточке файла стояла строка вида
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document` —
 * технически точная и совершенно нечитаемая. Незнакомый тип показывается как
 * есть: врать про формат хуже, чем показать его машинное имя.
 */
export function fileTypeLabel(mimeType: string | null | undefined): string {
  if (!mimeType) return "Тип неизвестен";
  const base = mimeType.split(";")[0]?.trim() ?? mimeType;
  return FILE_TYPE_LABELS[base] ?? base;
}
